/**
 * Switches between businesses. Small on purpose: most people have one workspace and should barely
 * notice this control, while a bookkeeper with five needs it on every page.
 */
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWorkspace } from "@/lib/session/workspace-context";

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { workspaces, workspace, select } = useWorkspace();
  const navigate = useNavigate();

  if (!workspace) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          // A long name truncates instead of widening the header.
          className="h-9 max-w-56 min-w-0 shrink justify-start gap-2 px-2 font-medium"
          data-testid="workspace-switcher"
        >
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{workspace.name}</span>
          <ChevronsUpDown className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("workspace.switcherLabel")}
        </DropdownMenuLabel>
        {workspaces.map((entry) => (
          <DropdownMenuItem
            key={entry.id}
            onClick={() => select(entry.id)}
            data-testid="workspace-option"
          >
            <span className="truncate">{entry.name}</span>
            {entry.id === workspace.id && <Check className="ml-auto size-4" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/workspaces/new")} data-testid="new-workspace">
          <Plus /> {t("workspace.create")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
